import { useLocation } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import { ExpandArrowIcon } from './sidebar.config';
import { isPathActive, hasActiveDescendant, handleMenuKeyDown } from './sidebar.utils';

// Skip .jpg — no transparency, so masking it would just paint a solid box.
const canMask = (src) => !/\.jpe?g($|\?)/i.test(src);

function ItemIcon({ item, recolor }) {
  if (!item.icon) return null;
  if (item.iconType === 'mui') {
    const Icon = item.icon;
    return <Icon className="sidebar-item-icon" />;
  }
  if (item.iconType === 'emoji') {
    return <span className="sidebar-item-icon sidebar-item-icon--emoji">{item.icon}</span>;
  }
  if (recolor && canMask(item.icon)) {
    return (
      <span
        className="sidebar-item-icon sidebar-item-icon--masked"
        style={{ WebkitMaskImage: `url(${item.icon})`, maskImage: `url(${item.icon})` }}
      />
    );
  }
  return <img className="sidebar-item-icon" src={item.icon} alt="" />;
}

export default function SidebarNavItem({
  item,
  depth,
  ctx,
  mode,
  state,
  statusCounts,
  onNavigate,
  ancestorKeys,
}) {
  const location = useLocation();

  if (item.visible && !item.visible(ctx)) return null;

  const hasChildren = !!item.children?.length;
  const isActive = isPathActive(item, location.pathname);
  const isOpen = hasChildren && state.isOpenAt(item.key);
  const isAncestorOfActive = hasChildren && hasActiveDescendant(item, location.pathname);

  const handleClick = () => {
    if (hasChildren) {
      if (mode === 'collapsed') {
        state.expandAndOpenPath([...ancestorKeys, item.key]);
      } else {
        state.toggleSection(item, ctx);
      }
      return;
    }
    item.onBeforeNavigate?.();
    onNavigate(item.path);
  };

  const onKeyDown = (event) =>
    handleMenuKeyDown(event, {
      hasChildren,
      isOpen,
      onOpen: () => state.toggleSection(item, ctx),
      onClose: () => state.toggleSection({ ...item, forceOpenForRoles: [] }, ctx),
    });

  const className = [
    'sidebar-item',
    `sidebar-item--depth-${depth}`,
    mode === 'collapsed' ? 'sidebar-item--collapsed' : 'sidebar-item--expanded',
    isActive ? 'sidebar-item--active' : '',
    isAncestorOfActive ? 'sidebar-item--ancestor-active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li role="none" className="sidebar-item-wrapper">
      <button
        type="button"
        role="menuitem"
        className={className}
        aria-current={!hasChildren && isActive ? 'page' : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        onClick={handleClick}
        onKeyDown={onKeyDown}
      >
        <ItemIcon item={item} recolor={isActive || isAncestorOfActive} />
        <span className="sidebar-item-label">{item.label}</span>
        {mode === 'expanded' && item.badgeStatusKey && (
          <StatusBadge item={item} ctx={ctx} statusCounts={statusCounts} active={isActive} />
        )}
        {hasChildren && mode === 'expanded' && (
          <ExpandArrowIcon
            className={`sidebar-chevron${isOpen ? ' sidebar-chevron--open' : ''}`}
          />
        )}
      </button>

      {hasChildren && isOpen && mode === 'expanded' && (
        <ul role="menu" data-depth={depth + 1} className={`sidebar-submenu sidebar-submenu--depth-${depth + 1}`}>
          {item.children.map((child) => (
            <SidebarNavItem
              key={child.key}
              item={child}
              depth={depth + 1}
              ctx={ctx}
              mode={mode}
              state={state}
              statusCounts={statusCounts}
              onNavigate={onNavigate}
              ancestorKeys={[...ancestorKeys, item.key]}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
